// Bell de notifications avec dropdown
import React, { useState, useEffect, useRef } from "react";
import { Bell, CheckCheck, Info, AlertTriangle, CheckCircle2, Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useRealtime } from "@/lib/realtime";
import { formatDateTime } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const SEVERITY = {
  info: { icon: Info, color: "text-citron" },
  success: { icon: CheckCircle2, color: "text-ok" },
  warning: { icon: AlertTriangle, color: "text-warn" },
  critical: { icon: Zap, color: "text-bad" },
};

export default function NotificationsBell() {
  const { notifications, unread, markRead, markAllRead, connected } = useRealtime();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const click = (n) => {
    markRead(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn-ghost h-9 w-9 p-0 relative"
        title="Notifications"
        data-testid="notifications-button"
      >
        <Bell size={15} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-citron text-obsidian-950 rounded-full text-[10px] font-bold leading-none grid place-items-center font-mono">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        {!connected && (
          <span className="absolute bottom-1 right-1 w-2 h-2 bg-bad rounded-full" title="Hors ligne" />
        )}
        {connected && unread === 0 && (
          <span className="absolute bottom-1 right-1 w-2 h-2 bg-ok rounded-full" title="Temps réel actif" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-12 w-96 max-h-[70vh] bg-obsidian-900 border border-line rounded-sm shadow-2xl z-50 overflow-hidden flex flex-col"
            data-testid="notifications-dropdown"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <div className="font-heading text-sm font-bold">Notifications</div>
                {connected ? (
                  <span className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-ok">
                    <span className="w-1.5 h-1.5 bg-ok rounded-full animate-pulse-glow" /> Live
                  </span>
                ) : (
                  <span className="text-[9px] font-mono uppercase tracking-wider text-bad">Hors ligne</span>
                )}
              </div>
              {unread > 0 && (
                <button onClick={markAllRead} className="btn-ghost h-7 text-[11px] px-2" data-testid="mark-all-read-button">
                  <CheckCheck size={12} /> Tout lire
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-sm text-obsidian-500">Aucune notification</div>
              ) : (
                notifications.slice(0, 30).map((n) => {
                  const sev = SEVERITY[n.severity] || SEVERITY.info;
                  const isRead = (n.read_by || []).length > 0;
                  return (
                    <button
                      key={n.id}
                      onClick={() => click(n)}
                      className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-obsidian-800 border-b border-line/50 text-left transition-colors ${isRead ? "opacity-60" : ""}`}
                      data-testid={`notification-${n.id}`}
                    >
                      <div className={`w-7 h-7 grid place-items-center rounded-sm flex-shrink-0 ${sev.color}`} style={{ background: "rgba(255,255,255,0.04)" }}>
                        <sev.icon size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{n.title}</div>
                        {n.body && <div className="text-xs text-obsidian-400 mt-0.5 line-clamp-2">{n.body}</div>}
                        <div className="text-[10px] font-mono text-obsidian-500 mt-1.5">
                          {n.from_user_name && `${n.from_user_name} · `}{formatDateTime(n.created_at)}
                        </div>
                      </div>
                      {!isRead && <span className="w-2 h-2 bg-citron rounded-full mt-2 flex-shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
